// Fracture Cantor (replaces `caster`) — tall robed technician-priest: blackened-blue robe, split ivory mask, a crystal
// tuning fork mounted along the right forearm. Wind-up: the fork arm raises and the orb between the prongs brightens.
// Elite (High Resonator): tall tuning-fork crown and a second fork on the left forearm (doubled apparatus).
import * as THREE from 'three/webgpu';
import { ArtBuilder, at, EnemyArt, limb, PAL, plate } from './kit';
import { ball, compassDip, gem, leg, noSkateSwing, V3 } from './parts_spec';

const STRIDE = 3.2, HIP = 0.95;
const SW = noSkateSwing(HIP, STRIDE);
const ROBE = 0x1d2440, ROBE_DARK = 0x141a30, ORB = 0xff4fc0, CRYSTAL = 0x7ff0e4;

/** forearm-mounted tuning fork; all parts rotate about the shoulder so the wind-up lifts it as one piece */
function forkArm(b: ArtBuilder, sx: number, raise: number, swing: number, sphase: number): void {
  const sh: V3 = [sx * 0.3, 1.58, 0], elbow: V3 = [sx * 0.34, 1.3, 0.04], wrist: V3 = [sx * 0.31, 1.22, 0.4];
  const o = { pivot: sh, raise, swing, sphase };
  b.add(limb(sh, elbow, 0.06, 0.055), { color: ROBE, ...o });
  b.add(limb([sh[0] + sx * 0.02, 1.42, 0.02], [elbow[0], 1.22, 0.06], 0.07, 0.11), { color: ROBE_DARK, ...o }); // sleeve
  b.add(limb(elbow, wrist, 0.055, 0.05), { color: PAL.ivoryShade, ...o });
  b.add(at(plate(0.14, 0.12, 0.34, 0.03, 1), sx * 0.325, 1.27, 0.22, 0.17), { color: PAL.ivory, ...o }); // bracer
  b.add(at(plate(0.26, 0.09, 0.1, 0.025, 1), wrist[0], wrist[1] + 0.01, 0.46), { color: PAL.copper, ...o }); // fork yoke
  for (const px of [-1, 1]) {
    const x = wrist[0] + px * 0.09;
    b.add(limb([x, 1.23, 0.48], [x, 1.26, 1.02], 0.042, 0.024, 6), { color: CRYSTAL, ...o });
    b.add(gem(0.045, 0.14, [x, 1.26, 1.07], Math.PI / 2), { color: PAL.teal, ...o });
  }
  b.add(at(new THREE.IcosahedronGeometry(0.07, 1), wrist[0], 1.245, 0.78), { color: ORB, glow: true, ...o }); // charging orb
}

function build(elite: boolean): ReturnType<ArtBuilder['build']> {
  const b = new ArtBuilder();
  const sw = elite ? noSkateSwing(HIP * 1.7, STRIDE) : SW;
  // legs (mostly under the robe; shins and boots show below the hem). +X is the Cantor's left.
  for (const s of [-1, 1]) {
    leg(b, {
      hip: [s * 0.12, HIP, 0], knee: [s * 0.13, 0.52, 0.07], ankle: [s * 0.12, 0.1, 0],
      rThigh: 0.08, rShin: 0.065, foot: [0.14, 0.08, 0.3], toe: 0.06,
      thigh: ROBE_DARK, shin: PAL.iron, kneeCol: PAL.iron, footCol: PAL.ironLight,
      swing: sw, sphase: s > 0 ? 0 : Math.PI,
    });
  }
  // robe skirt, tabard and belt
  b.add(at(new THREE.CylinderGeometry(0.25, 0.42, 0.86, 10, 1, true), 0, 0.69, 0), { color: ROBE });
  b.add(at(new THREE.CylinderGeometry(0.43, 0.43, 0.05, 10, 1, true), 0, 0.27, 0), { color: PAL.ivoryShade }); // hem band
  b.add(at(plate(0.18, 0.78, 0.04, 0.015, 1), 0, 0.7, 0.35, -0.19), { color: PAL.ivoryShade });
  b.add(at(plate(0.1, 0.5, 0.045, 0.012, 1), 0, 0.78, 0.36, -0.19), { color: elite ? PAL.gold : PAL.copper });
  b.add(at(new THREE.CylinderGeometry(0.27, 0.27, 0.09, 10), 0, 1.12, 0), { color: PAL.copperDark });
  // torso, chest plate, mantle
  b.add(limb([0, 1.1, 0], [0, 1.62, 0], 0.23, 0.27, 10), { color: ROBE });
  b.add(at(plate(0.34, 0.3, 0.1, 0.03, 1), 0, 1.44, 0.2, -0.08), { color: PAL.ivory });
  b.add(at(plate(0.1, 0.1, 0.04, 0.02, 1), 0, 1.44, 0.26), { color: CRYSTAL });
  for (const s of [-1, 1]) b.add(at(plate(0.22, 0.07, 0.32, 0.03, 1), s * 0.27, 1.63, 0, 0, 0, s * -0.38), { color: PAL.ivory });
  b.add(limb([0, 1.62, 0], [0, 1.75, 0.01], 0.07, 0.065), { color: PAL.iron });
  // hooded head with a split ceramic mask (two offset halves, magenta seam between them)
  b.add(ball(0.17, [0, 1.86, -0.03], [1, 1.12, 1.05], 10, 8), { color: ROBE });
  b.add(at(new THREE.ConeGeometry(0.13, 0.26, 8), 0, 2.03, -0.08, -0.45), { color: ROBE });
  b.add(at(plate(0.11, 0.27, 0.05, 0.02, 1), 0.07, 1.85, 0.15, 0, 0.3, 0.05), { color: PAL.ivory });
  b.add(at(plate(0.11, 0.25, 0.05, 0.02, 1), -0.075, 1.875, 0.145, 0, -0.3, -0.07), { color: PAL.ivoryShade });
  b.add(at(plate(0.022, 0.22, 0.02, 0.008, 1), 0, 1.86, 0.14), { color: ORB, glow: true });
  for (const s of [-1, 1]) b.add(at(plate(0.055, 0.016, 0.02, 0.006, 1), s * 0.07, 1.9, 0.18, 0, s * 0.3), { color: ORB, glow: true });
  // arms: the right (-X) carries the fork; the left hangs in a wide sleeve and swings
  forkArm(b, -1, -1.05, 0.06, 0);
  if (elite) forkArm(b, 1, -1.05, 0.06, Math.PI);
  else {
    const sh: V3 = [0.3, 1.58, 0], o = { pivot: sh, swing: 0.35, sphase: Math.PI };
    b.add(limb(sh, [0.33, 1.28, 0.02], 0.055, 0.05), { color: ROBE, ...o });
    b.add(limb([0.32, 1.4, 0], [0.35, 1.02, 0.05], 0.07, 0.13), { color: ROBE_DARK, ...o });
    b.add(ball(0.055, [0.35, 0.98, 0.06], [1, 1.2, 1], 8, 6), { color: PAL.ivoryShade, ...o });
  }
  if (elite) {
    // High Resonator: tall fork crown above the hood with its own resonance orb, and a raised gold collar
    for (const s of [-1, 1]) {
      b.add(limb([s * 0.05, 1.98, -0.04], [s * 0.12, 2.55, -0.08], 0.035, 0.025, 6), { color: PAL.gold });
      b.add(gem(0.05, 0.22, [s * 0.125, 2.64, -0.08]), { color: CRYSTAL });
    }
    b.add(at(plate(0.2, 0.06, 0.08, 0.02, 1), 0, 1.99, -0.04), { color: PAL.gold });
    b.add(at(new THREE.IcosahedronGeometry(0.06, 1), 0, 2.36, -0.06), { color: ORB, glow: true });
    for (const s of [-1, 1]) b.add(at(plate(0.16, 0.3, 0.04, 0.02, 1), s * 0.14, 1.8, -0.16, 0.2, s * 0.5), { color: PAL.gold });
    for (const s of [-1, 1]) b.add(gem(0.06, 0.26, [s * 0.36, 1.74, -0.04], 0, 0, s * -0.5), { color: CRYSTAL });
  }
  return b.build();
}

export const ART: EnemyArt = { name: 'Fracture Cantor', build, stride: STRIDE, bob: compassDip(HIP, SW) * 0.85 };
